import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import 'react-loading-skeleton/dist/skeleton.css';

import userPlaceholder from '../../assets/user.png';
import AssociatedAccount from '../../components/AssociatedAccount';
import GuestNavList from '../../components/GuestNavList';
import PaymentPage from '../../components/PaymentPage';
import ProfileData from '../../components/ProfileData';
import env from '../../config/env';

import { useUser } from '../../utils/hook/UserContext';
import { useAxios } from '../../utils/hook/useAxios';

import './mc-guest-settings.css';

const STATUS_BADGE = {
  missing: { tone: 'danger', icon: 'bi-exclamation-circle-fill', label: 'Manquant' },
  pending: { tone: 'warn', icon: 'bi-hourglass-split', label: 'En vérification' },
  approved: { tone: 'ok', icon: 'bi-check-circle-fill', label: 'Validé' },
  rejected: { tone: 'danger', icon: 'bi-x-circle-fill', label: 'Refusé' },
};

/**
 * L'API renvoie des booléens (true/false), alors que l'ancien code
 * comparait avec la chaîne '1'. On accepte toutes les représentations.
 */
const isTrue = (value) => value === true || value === 1 || value === '1' || value === 'true';

const isFalse = (value) => value === false || value === 0 || value === '0' || value === 'false';

/** URL valide = chaîne non vide */
const cleanUrl = (value) => (typeof value === 'string' && value.trim() !== '' ? value : null);

/** Statut d'un document à partir de son URL et de son flag de validation */
const getDocStatus = (docUrl, validatedFlag) => {
  if (!docUrl) return 'missing';
  if (isTrue(validatedFlag)) return 'approved';
  return 'pending';
};

/** Champ "validé" associé à chaque champ document */
const VALIDATION_FIELD = {
  PermisConduireDoc: 'PermisConduire',
  PieceIdentityDoc: 'PieceIdentite',
};

function DocCard({
  label,
  field,
  docUrl,
  status,
  inputRef,
  uploading,
  uploadPct,
  uploadDoc,
  deleteDoc,
}) {
  const badge = STATUS_BADGE[status] ?? STATUS_BADGE.missing;
  const isImg = docUrl && /\.(jpe?g|png|webp|gif)(\?|$)/i.test(docUrl);
  const busy = Boolean(uploading[field]);
  const pct = uploadPct[field] ?? 0;

  return (
    <div className="mc-gs-doc">
      <div className="mc-gs-doc-head">
        <span className="mc-gs-doc-label">{label}</span>
        <span className={`mc-gs-pill mc-gs-pill--${badge.tone}`}>
          <i className={`bi ${badge.icon}`} aria-hidden="true"></i>
          {badge.label}
        </span>
      </div>

      {busy && (
        <div className="mc-gs-progress">
          <div className="mc-gs-progress-top">
            <span>Envoi en cours…</span>
            <strong>{pct}%</strong>
          </div>
          <div
            className="mc-gs-progress-bar"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="mc-gs-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {docUrl ? (
        <>
          <div className="mc-gs-doc-preview">
            {isImg ? <img src={docUrl} alt={label} /> : <iframe src={docUrl} title={label} />}
          </div>

          <div className="mc-gs-doc-actions">
            <button
              type="button"
              className="mc-btn mc-btn--secondary mc-btn--sm"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
            >
              {busy ? (
                <>
                  <span className="mc-gs-spin" aria-hidden="true"></span>
                  Envoi…
                </>
              ) : (
                <>
                  <i className="bi bi-pencil" aria-hidden="true"></i>
                  Modifier
                </>
              )}
            </button>
            <button
              type="button"
              className="mc-btn mc-btn--sm mc-gs-danger"
              onClick={() => deleteDoc(field)}
              disabled={busy}
              aria-label={`Supprimer : ${label}`}
            >
              <i className="bi bi-trash" aria-hidden="true"></i>
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="mc-gs-doc-empty">
            <i className="bi bi-file-earmark-arrow-up" aria-hidden="true"></i>
            Aucun document
          </div>
          <button
            type="button"
            className="mc-btn mc-btn--secondary mc-btn--sm mc-btn--block"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            {busy ? (
              <>
                <span className="mc-gs-spin" aria-hidden="true"></span>
                Envoi…
              </>
            ) : (
              <>
                <i className="bi bi-cloud-upload" aria-hidden="true"></i>
                Téléverser
              </>
            )}
          </button>
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf"
        className="d-none"
        aria-label={`Fichier : ${label}`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) uploadDoc(file, field);
          e.target.value = '';
        }}
      />
    </div>
  );
}

const GoogleLogo = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.32-8.16 2.32-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

/**
 * Modal affiché après l'enregistrement d'une carte.
 * Il vit dans la page (et non dans PaymentPage) car PaymentPage est démonté
 * dès que la carte est enregistrée.
 */
function CardSavedModal({ last4, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="mc-gs-modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="mc-gs-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mc-gs-modal-title"
      >
        <div className="mc-gs-modal-icon" aria-hidden="true">
          <i className="bi bi-check-lg"></i>
        </div>

        <h2 id="mc-gs-modal-title" className="mc-gs-modal-title">
          Carte enregistrée
        </h2>
        <p className="mc-gs-modal-text">
          Votre carte a bien été enregistrée. Aucun débit n’a été effectué. Elle sera utilisée pour
          régler vos réservations.
        </p>

        {last4 && <p className="mc-gs-modal-card">•••• •••• •••• {last4}</p>}

        <button
          ref={closeRef}
          type="button"
          className="mc-btn mc-btn--primary mc-btn--block"
          onClick={onClose}
        >
          Terminé
        </button>
      </div>
    </div>
  );
}

function GuestSetting() {
  const axios = useAxios();

  const { user: authUser, loading, isAuthenticated } = useUser();

  const [user, setUser] = useState(authUser ?? null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setOnboardingUrl] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [deleteCardLoading, setDeleteCardLoading] = useState(false);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [cardJustSaved, setCardJustSaved] = useState(false);

  const [uploading, setUploading] = useState({});
  const [uploadPct, setUploadPct] = useState({});

  const permisInputRef = useRef(null);
  const identityInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  // Fusion (et non remplacement) : on ne perd pas les champs déjà chargés
  useEffect(() => {
    if (authUser) setUser((current) => ({ ...(current ?? {}), ...authUser }));
  }, [authUser]);

  useEffect(() => {
    if (!user?.id) return;

    const fetchUser = async () => {
      try {
        const response = await axios.get(`/users/searchbyid/${user.id}`);
        if (response.data) setUser((current) => ({ ...(current ?? {}), ...response.data }));
      } catch (error) {
        console.error('Erreur lors de la requête Axios :', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [user?.id, axios]);

  /**
   * Recharge l'utilisateur depuis l'API.
   *
   * - `overrides` : valeurs que l'on sait justes (ex. l'URL qu'on vient
   *   d'envoyer). Elles sont appliquées APRÈS la réponse du serveur, afin
   *   qu'une réponse en cache ou en retard ne les écrase pas.
   * - `_t` : anti-cache, pour éviter qu'un GET déjà en cache (304) renvoie
   *   l'ancien état de l'utilisateur.
   */
  const refreshUser = async (overrides = {}) => {
    if (!user?.id) return;

    const response = await axios.get(`/users/searchbyid/${user.id}`, {
      params: { _t: Date.now() },
    });

    setUser((current) => ({
      ...(current ?? {}),
      ...(response.data ?? {}),
      ...overrides,
    }));
  };

  const stopUploading = (field) => {
    setUploading((prev) => ({ ...prev, [field]: false }));
    setUploadPct((prev) => ({ ...prev, [field]: 0 }));
  };

  const uploadDoc = (file, field) => {
    if (!file || !user?.id) return;

    setUploading((prev) => ({ ...prev, [field]: true }));
    setUploadPct((prev) => ({ ...prev, [field]: 0 }));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'r6s4af83');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', 'https://api.cloudinary.com/v1_1/dtrkcpsky/auto/upload');

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded / event.total) * 100);
        setUploadPct((prev) => ({ ...prev, [field]: percentage }));
      }
    };

    xhr.onload = async () => {
      try {
        if (xhr.status < 200 || xhr.status >= 300) {
          throw new Error(`Cloudinary upload failed with status ${xhr.status}`);
        }

        const data = JSON.parse(xhr.responseText);
        const newUrl = data.secure_url;
        const validationField = VALIDATION_FIELD[field];

        // Un nouveau document doit être revalidé
        const resetField = validationField ? { [validationField]: '0' } : {};

        await axios.put(`/users/${user.id}`, { [field]: newUrl, ...resetField });

        // Valeurs locales connues (le document est enregistré, pas encore validé)
        const localOverrides = {
          [field]: newUrl,
          ...(validationField ? { [validationField]: false } : {}),
        };

        // 1) Mise à jour immédiate de l'écran, sans attendre un nouveau GET
        setUser((current) => ({ ...(current ?? {}), ...localOverrides }));

        // 2) Resynchronisation avec le serveur (sans écraser ce qu'on vient d'envoyer)
        try {
          await refreshUser(localOverrides);
        } catch (refreshError) {
          console.error('Rafraîchissement après upload échoué :', refreshError);
        }
      } catch (error) {
        console.error('Upload échoué :', error);
      } finally {
        stopUploading(field);
      }
    };

    xhr.onerror = () => {
      console.error('Erreur réseau upload');
      stopUploading(field);
    };

    xhr.send(formData);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStripeLoading(true);

    try {
      const response = await axios.post('/stripe/create-connected-account');
      setOnboardingUrl(response.data.onboardingUrl);

      await refreshUser();
      setIsLoading(false);

      if (response.data.onboardingUrl) {
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setStripeLoading(false);
    }
  };

  // Appelé par PaymentPage une fois la carte enregistrée (Stripe + serveur OK)
  const handleCardSaved = async (result) => {
    // Selon la réponse de /stripe/confirm-card-setup, les infos de la carte
    // peuvent être à la racine ou dans `card`.
    const card = result?.card ?? result ?? {};
    const overrides = {};
    if (card.last4) overrides.last4 = card.last4;
    if (card.exp_month) overrides.exp_month = card.exp_month;
    if (card.exp_year) overrides.exp_year = card.exp_year;

    // Retour visuel immédiat : statut + modal
    setCardJustSaved(true);
    setCardModalOpen(true);
    setIsLoading(false);
    if (Object.keys(overrides).length) {
      setUser((current) => ({ ...(current ?? {}), ...overrides }));
    }

    // Resynchronisation avec le serveur (sans écraser ce qu'on sait déjà)
    try {
      await refreshUser(overrides);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteCard = async () => {
    setDeleteCardLoading(true);
    try {
      await axios.post('/stripe/delete-card');
      setCardJustSaved(false);
      setCardModalOpen(false);
      await refreshUser({ last4: null, exp_month: null, exp_year: null });
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteCardLoading(false);
    }
  };

  const deleteDoc = async (field) => {
    if (!user?.id) return;

    const validationField = VALIDATION_FIELD[field];
    const resetField = validationField ? { [validationField]: '0' } : {};

    try {
      await axios.put(`/users/${user.id}`, { [field]: null, ...resetField });
      setUser((currentUser) => ({
        ...currentUser,
        [field]: null,
        ...(validationField ? { [validationField]: false } : {}),
      }));
    } catch (error) {
      console.error('Erreur lors de la suppression du document :', error);
    }
  };

  const handleUnlinkGoogle = async () => {
    if (!window.confirm('Délier votre compte Google ?')) return;
    try {
      await axios.delete(`/users/${user.id}/unlink-google`);
      setUser((currentUser) => ({ ...currentUser, googleId: null }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    setUploading((prev) => ({ ...prev, avatar: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'r6s4af83');

      const response = await fetch('https://api.cloudinary.com/v1_1/dtrkcpsky/image/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Cloudinary upload failed with status ${response.status}`);
      }

      const data = await response.json();

      await axios.put(`/users/${user.id}`, { profile_url: data.secure_url });

      setUser((currentUser) => ({ ...currentUser, profile_url: data.secure_url }));

      const stored = JSON.parse(localStorage.getItem('me') || '{}');
      localStorage.setItem('me', JSON.stringify({ ...stored, profile_url: data.secure_url }));
    } catch (error) {
      console.error('Upload avatar échoué :', error);
    } finally {
      setUploading((prev) => ({ ...prev, avatar: false }));
      event.target.value = '';
    }
  };

  const initials = user
    ? `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase()
    : '';

  // ── Documents (URL + statut) ────────────────────────────────────────────
  const permisDocUrl = cleanUrl(user?.PermisConduireDoc);
  const identityDocUrl = cleanUrl(user?.PieceIdentityDoc ?? user?.PieceIdentiteDoc);

  const permisStatus = getDocStatus(permisDocUrl, user?.PermisConduire);
  const identityStatus = getDocStatus(identityDocUrl, user?.PieceIdentite);

  // ── Statut du compte (booléen ou '0'/'1') ───────────────────────────────
  const accountVerified = isTrue(user?.verified);
  const accountPending = user?.verified === '0';
  const accountActionRequired = !accountVerified && !accountPending;

  // ── Compte Stripe (versements) ──────────────────────────────────────────
  const hasStripeAccount = Boolean(user?.stripeAccountId || user?.stripeBankAccountId);

  // ── Carte de paiement enregistrée ───────────────────────────────────────
  const hasCard = Boolean(user?.last4) || cardJustSaved;

  if (loading) return null;
  if (!isAuthenticated && !user) return null;

  return (
    <main className="mc-home mc-guest-page">
      <div className="container mc-container">
        <div className="mc-guest-layout">
          {/* ════ Sidebar — desktop ════ */}
          <aside className="mc-guest-aside" aria-label="Espace invité">
            <div className="mc-guest-card">
              <GuestNavList />
            </div>
          </aside>

          {/* ════ Contenu ════ */}
          <section className="mc-gs-stack" aria-label="Paramètres du compte">
            {/* Hero */}
            <div className="mc-gs-hero">
              <div className="mc-gs-avatar-wrap">
                <div className="mc-gs-avatar">
                  {user?.profile_url ? (
                    <img src={user.profile_url} alt="Photo de profil" />
                  ) : initials ? (
                    initials
                  ) : (
                    <img src={userPlaceholder} alt="Photo de profil" />
                  )}
                </div>

                <button
                  type="button"
                  className="mc-gs-avatar-edit"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploading.avatar}
                  aria-label="Modifier la photo de profil"
                  title="Modifier la photo"
                >
                  {uploading.avatar ? (
                    <span className="mc-gs-spin" aria-hidden="true"></span>
                  ) : (
                    <i className="bi bi-pencil-fill" aria-hidden="true"></i>
                  )}
                </button>

                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  aria-label="Choisir une photo de profil"
                  onChange={handleAvatarUpload}
                />
              </div>

              <div style={{ minWidth: 0 }}>
                <h1 className="mc-gs-hero-name">
                  {user?.firstName} {user?.lastName}
                </h1>
                <p className="mc-gs-hero-mail">{user?.email}</p>
              </div>
            </div>

            {/* Statut du compte */}
            {accountPending && (
              <div className="mc-gs-banner" role="status">
                <i className="bi bi-clock-history" aria-hidden="true"></i>
                <span>
                  Votre compte est en cours de vérification. Cette étape permet de sécuriser la
                  plateforme.
                </span>
              </div>
            )}

            {accountVerified && (
              <div className="mc-gs-banner mc-gs-banner--ok" role="status">
                <i className="bi bi-check2-circle" aria-hidden="true"></i>
                <span>Votre compte est vérifié et pleinement opérationnel.</span>
              </div>
            )}

            {accountActionRequired && (
              <div className="mc-gs-banner mc-gs-banner--danger" role="alert">
                <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
                <span>Une action est requise pour activer votre compte.</span>
              </div>
            )}

            {/* Documents */}
            <div className="mc-gs-card">
              <div className="mc-gs-card-head">
                <div>
                  <h2 className="mc-gs-card-title">Documents</h2>
                  <p className="mc-gs-card-desc">
                    Permis et pièce d’identité, nécessaires pour réserver un véhicule.
                  </p>
                </div>
              </div>

              <div className="mc-gs-docs">
                <DocCard
                  label="Permis de conduire"
                  field="PermisConduireDoc"
                  docUrl={permisDocUrl}
                  status={permisStatus}
                  inputRef={permisInputRef}
                  uploading={uploading}
                  uploadPct={uploadPct}
                  uploadDoc={uploadDoc}
                  deleteDoc={deleteDoc}
                />

                <DocCard
                  label="Pièce d’identité"
                  field="PieceIdentityDoc"
                  docUrl={identityDocUrl}
                  status={identityStatus}
                  inputRef={identityInputRef}
                  uploading={uploading}
                  uploadPct={uploadPct}
                  uploadDoc={uploadDoc}
                  deleteDoc={deleteDoc}
                />
              </div>
            </div>

            <ProfileData user={user} />

            <AssociatedAccount data={user} />

            {/* Comptes liés */}
            <div className="mc-gs-card">
              <div className="mc-gs-card-head">
                <h2 className="mc-gs-card-title">Comptes liés</h2>
              </div>

              <div className="mc-gs-linked">
                <div className="mc-gs-linked-id">
                  <span className="mc-gs-linked-icon">
                    <GoogleLogo />
                  </span>
                  <div>
                    <p className="mc-gs-linked-name">Google</p>
                    {user?.googleId ? (
                      <p className="mc-gs-linked-state is-ok">
                        <i className="bi bi-check-circle-fill" aria-hidden="true"></i> Compte lié
                      </p>
                    ) : (
                      <p className="mc-gs-linked-state">Non lié</p>
                    )}
                  </div>
                </div>

                {user?.googleId ? (
                  <button
                    type="button"
                    className="mc-btn mc-btn--sm mc-gs-danger"
                    onClick={handleUnlinkGoogle}
                  >
                    Délier
                  </button>
                ) : (
                  <a
                    href={`${env.baseUrlApi}/api/auth/google`}
                    className="mc-btn mc-btn--secondary mc-btn--sm"
                  >
                    <i className="bi bi-google" aria-hidden="true"></i>
                    Connecter
                  </a>
                )}
              </div>
            </div>

            {/* Informations bancaires */}
            <div className="mc-gs-card">
              <div className="mc-gs-card-head mc-gs-card-head--split">
                <div>
                  <h2 className="mc-gs-card-title">Vos informations bancaires</h2>
                  <p className="mc-gs-card-desc">Carte utilisée pour régler vos réservations.</p>
                </div>

                {!isLoading && (
                  <span className={`mc-gs-pill mc-gs-pill--${hasCard ? 'ok' : 'warn'}`}>
                    <i
                      className={`bi ${hasCard ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`}
                      aria-hidden="true"
                    ></i>
                    {hasCard ? 'Carte enregistrée' : 'Aucune carte'}
                  </span>
                )}
              </div>

              {cardJustSaved && (
                <div className="mc-gs-banner mc-gs-banner--ok" role="status">
                  <i className="bi bi-check2-circle" aria-hidden="true"></i>
                  <span>Votre carte a bien été enregistrée.</span>
                </div>
              )}

              {isLoading ? (
                <div className="mc-gs-skeleton" aria-hidden="true"></div>
              ) : (
                <>
                  {!hasCard && <PaymentPage email={user?.email} onCardSave={handleCardSaved} />}

                  {hasCard && (
                    <div className="mc-gs-saved-card">
                      <div className="mc-gs-cc">
                        <span className="mc-gs-cc-chip" aria-hidden="true"></span>
                        <p className="mc-gs-cc-num">•••• •••• •••• {user?.last4 ?? '••••'}</p>
                        {user?.exp_month && user?.exp_year && (
                          <p className="mc-gs-cc-exp">
                            Expiration : {user.exp_month}/{user.exp_year}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        className="mc-btn mc-btn--sm mc-gs-danger"
                        onClick={handleDeleteCard}
                        disabled={deleteCardLoading}
                      >
                        {deleteCardLoading ? (
                          <span className="mc-gs-spin" aria-hidden="true"></span>
                        ) : (
                          <>
                            <i className="bi bi-trash" aria-hidden="true"></i>
                            Supprimer
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Compte Stripe */}
            {user && (
              <div className="mc-gs-card">
                <div className="mc-gs-card-head">
                  <div>
                    <h2 className="mc-gs-card-title">Compte bancaire (versements)</h2>
                    <p className="mc-gs-card-desc">
                      Connectez votre compte pour recevoir vos gains.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mc-gs-fields mc-gs-fields--one">
                    <div>
                      <label className="mc-field-label" htmlFor="gs-stripe-email">
                        Votre adresse email
                      </label>
                      <input
                        id="gs-stripe-email"
                        className="mc-input"
                        type="text"
                        disabled
                        value={user?.email ?? ''}
                        required
                      />
                    </div>
                  </div>

                  {!hasStripeAccount && (
                    <button
                      className="mc-btn mc-btn--primary mc-gs-submit"
                      type="submit"
                      disabled={stripeLoading}
                    >
                      {stripeLoading ? (
                        <>
                          <span className="mc-gs-spin" aria-hidden="true"></span>
                          Connexion en cours…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-bank" aria-hidden="true"></i>
                          Connecter mon compte bancaire
                        </>
                      )}
                    </button>
                  )}

                  {hasStripeAccount && (
                    <div className="mc-gs-banner mc-gs-banner--ok mc-gs-submit" role="status">
                      <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                      <span style={{ flex: 1 }}>
                        Compte Stripe connecté
                        {user?.bankLast4 ? ` (•••• ${user.bankLast4})` : ''}
                      </span>
                      <Link to="/business/earnings" className="mc-btn mc-btn--primary mc-btn--sm">
                        Voir mes gains
                      </Link>
                    </div>
                  )}
                </form>
              </div>
            )}
          </section>
        </div>
      </div>

      {cardModalOpen && (
        <CardSavedModal last4={user?.last4} onClose={() => setCardModalOpen(false)} />
      )}

      {/* Nav mobile bas de page */}
      <div className="mc-guest-bottombar">
        <GuestNavList />
      </div>
    </main>
  );
}

export default GuestSetting;
